import { STD_TEST_COURSE_SLUGS } from 'my-core/course-utils';

import { CALENDLY_SALES_DESTINATIONS } from './calendly-config';

type Properties = Readonly<Record<string, unknown>>;
type PropertyMapping = Readonly<Record<string, string | ((properties: Properties) => unknown)>>;
type EventConfig = Readonly<{
  event_id: string;
  property_mapping: PropertyMapping;
  filter?: (properties: Properties) => boolean;
}>;

const SALES_DESTINATIONS: Record<string, string> = Object.fromEntries(
  Object.entries(CALENDLY_SALES_DESTINATIONS).flatMap(([exam, destinations]) =>
    Object.values(destinations).map(path => [`/${path}`, exam.toUpperCase()]),
  ),
);

function salesDestination(properties: Properties): URL | undefined {
  if (typeof properties.destination_url !== 'string') return;
  try {
    const url = new URL(properties.destination_url);
    if (url.origin === 'https://calendly.com' && !url.username && !url.password && SALES_DESTINATIONS[url.pathname])
      return url;
  } catch {
    // An unrecognized destination is not sales intent.
  }
  return undefined;
}

const productProperties: PropertyMapping = {
  exam: 'exam',
  product_id: props => (props.product_id == null ? undefined : String(props.product_id)),
  product_slug: 'product_slug',
};
const examProduct = (props: Properties): boolean =>
  props.product_id != null && typeof props.exam === 'string' && props.exam.length > 0;

function checkoutConfig(action: string, exam?: string): EventConfig {
  return {
    event_id: `pe47437452_wp_checkout_${action}`,
    property_mapping: exam ? { ...productProperties, exam: () => exam } : productProperties,
    filter: exam ? props => props.product_id != null : examProduct,
  };
}

// Keys are existing trackCustomEvent names. Mapping keys are HubSpot properties; values are source
// property names or small transforms. A filter is only needed when not every occurrence qualifies.
let config: Readonly<Record<string, EventConfig>> | undefined;

// Build after module initialization because course-utils also imports the analytics pipeline indirectly.
export function getHubspotEventsConfig(): Readonly<Record<string, EventConfig>> {
  return (config ??= {
    ...Object.fromEntries(
      STD_TEST_COURSE_SLUGS.flatMap(slug => {
        const exam = slug.toUpperCase();
        return ['Viewed', 'Attempted'].map(action => [
          `${exam} Checkout ${action}`,
          checkoutConfig(action.toLowerCase(), exam),
        ]);
      }),
    ),
    'Custom Product Checkout Viewed': checkoutConfig('viewed'),
    'Custom Product Checkout Attempted': checkoutConfig('attempted'),
    'Calendly Link Clicked': {
      event_id: 'pe47437452_wp_sales_booking_link_clicked',
      property_mapping: {
        exam: props => {
          const url = salesDestination(props);
          return url && SALES_DESTINATIONS[url.pathname];
        },
        destination_url: props => {
          const url = salesDestination(props);
          return url && url.origin + url.pathname;
        },
      },
      filter: props => !!salesDestination(props),
    },
  });
}
